import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DeploymentImpactTimeline } from './DeploymentImpactTimeline';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { LinkedIncidentsTable } from './incidents/LinkedIncidentsTable';
import { DeploymentIncidentList } from './incidents/DeploymentIncidentList';
import { DatewiseDeploymentTable } from './DatewiseDeploymentTable';
import { useDeploymentTimeline } from '@/hooks/useDeploymentTimeline';

interface LinkedIncident {
  task_number: string;
  api_name: string;
  incident_number: string;
  incident_description: string;
  incident_created_date: string;
  priority: string;
  status: string;
}

interface PostDeploymentIncidentsProps {
  linkedIncidents: LinkedIncident[];
  title?: string;
  showFullAnalytics?: boolean;
  catalogTasks?: any[];
  incidents?: any[];
}

export function PostDeploymentIncidents({ 
  linkedIncidents, 
  title = "",
  showFullAnalytics = false,
  catalogTasks = [],
  incidents = []
}: PostDeploymentIncidentsProps) {
  const [selectedApi, setSelectedApi] = useState<string>("all");
  const [isOpen, setIsOpen] = useState(showFullAnalytics || false);
  const [selectedTimelineApi, setSelectedTimelineApi] = useState<string>("");

  // Process data for both tables using the same approach
  const excelData = React.useMemo(() => ({
    'Catalog Tasks': catalogTasks || [],
    'Incidents': incidents || []
  }), [catalogTasks, incidents]);

  // Use the same timeline data processing for both tables
  const timelineDataByApi = useDeploymentTimeline(excelData);

  // Process linkedIncidents data to match the format expected
  const processedLinkedIncidents = React.useMemo(() => {
    // Group incidents by API name to match the format expected by LinkedIncidentsTable
    const groupedByApi: Record<string, LinkedIncident[]> = {};
    
    linkedIncidents.forEach(incident => {
      const apiName = incident.api_name || "Unknown API";
      
      if (!groupedByApi[apiName]) {
        groupedByApi[apiName] = [];
      }
      
      groupedByApi[apiName].push(incident);
    });
    
    return Object.entries(groupedByApi).sort((a, b) => b[1].length - a[1].length);
  }, [linkedIncidents]);

  // Calculate total number of deployments from timeline data
  const totalDeployments = React.useMemo(() => {
    let total = 0;
    timelineDataByApi.forEach((apiData) => {
      apiData.forEach((day) => {
        total += day.deploymentDetails?.length || 0;
      });
    });
    return total;
  }, [timelineDataByApi]);

  // Count deployment-related catalog tasks
  const deploymentTaskCount = React.useMemo(() => {
    const deploymentKeywords = ["deploy", "deployment", "release", "rollout", "publish", "redeploy", "promote"];
    return catalogTasks.filter(task => {
      const description = (task["Short description"] || task.description || "").toLowerCase();
      return deploymentKeywords.some(keyword => description.includes(keyword));
    }).length;
  }, [catalogTasks]);

  return (
    <div className="space-y-6">
      <DatewiseDeploymentTable 
        catalogTasks={catalogTasks}
        incidents={incidents}
        linkedIncidents={linkedIncidents}
      />

      <Card>
      
        <CardContent>
          <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">
                {isOpen ? "Hide" : "Show"} Deployment-Related Incidents
              </div>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-9 p-0">
                  <ChevronDown className={`h-4 w-4 ${isOpen ? "rotate-180" : "rotate-0"} transition-all`} />
                </Button>
              </CollapsibleTrigger>
            </div>
            
            <CollapsibleContent className="space-y-4">
              <LinkedIncidentsTable 
                incidentsByApi={processedLinkedIncidents}
                catalogTasks={catalogTasks}
              />
            </CollapsibleContent> 
          </Collapsible>
        </CardContent>
      </Card>
    </div>
  );
}
